/**
 * Helpers for the "Create event from poster" flow.
 *
 *   emptyBase         — a valid empty seed skeleton (the BFF now supplies history defaults).
 *   mergeIntake       — overlay the agent's confident values onto the skeleton.
 *   mapModelToFormValues — normalise dates (dayjs, drop past) → native form values.
 *
 * The skeleton is always valid on its own; the agent + BFF only enrich it.
 */
import dayjs from "dayjs";

const HSN_CODE = "999723";

function newLocation() {
  return {
    country_code: "IN",
    name: "",
    address_line_1: "",
    city: "",
    state: "",
    postal_code: "",
    geo_location: { lat: undefined, lng: undefined },
  };
}

export function emptyBase() {
  return {
    model: {
      community_id: undefined,
      event_type: "CONCERT",
      event_access: "PUBLIC",
      title: "",
      description: "",
      tags: [],
      is_featured: false,
      // The poster flow never sets this; new events are listed by default.
      show_in_listings: true,
      start_date_time: undefined,
      end_date_time: undefined,
      location_mode: "new",
      location_id: undefined,
      location: newLocation(),
      organiser_name: "",
      organiser_logo_url: undefined,
      contact_email: "",
      contact_phone: "",
      activities: [],
      amenities: [],
      tickets: [{ name: "General Admission", price: 0, quantity: 50 }],
      media: [],
      hsn_code: HSN_CODE,
    },
    sources: {},
  };
}

/** Overlay the agent's confident values onto the deterministic base. */
export function mergeIntake(model, agentEvent) {
  if (!agentEvent || typeof agentEvent !== "object") return model;
  const out = { ...model };
  for (const [key, value] of Object.entries(agentEvent)) {
    if (value === null) continue;
    if (key === "tickets" && Array.isArray(value)) {
      // The agent often returns tiers with only name + price; keep the app's
      // defaults so capacity/price never come back blank.
      if (value.length)
        out.tickets = value.map((t) => ({
          name: t?.name ?? "General Admission",
          price: t?.price ?? 0,
          quantity: t?.quantity ?? 50,
        }));
    } else if (Array.isArray(value)) {
      if (value.length) out[key] = value;
    } else if (key === "location" && typeof value === "object") {
      out.location = { ...(model.location || {}), ...value };
    } else {
      out[key] = value;
    }
  }

  // A matched saved venue → "existing"; a free-text venue → "new".
  if (out.location_id) out.location_mode = "existing";
  else if (agentEvent.location && typeof agentEvent.location === "object")
    out.location_mode = "new";

  return out;
}

/**
 * Required fields the organiser still has to complete after pre-fill. Operates on
 * the mapped form values (mapModelToFormValues output), so a dropped past date
 * correctly counts as missing. Returns human-readable labels for a "still needs" nudge.
 */
export function getMissingRequiredFields(values) {
  if (!values) return [];
  const missing = [];
  if (!values.title) missing.push("Event title");
  if (!values.start_date_time) missing.push("Event date & time");
  if (values.event_access === "MEMBERS_ONLY" && !values.community_id)
    missing.push("Community");
  const hasVenue =
    values.location_id ||
    (values.location && (values.location.name || values.location.address_line_1));
  if (!hasVenue) missing.push("Venue");
  const tickets = values.tickets ?? [];
  if (!tickets.length) missing.push("Ticket details");
  else if (
    tickets.some(
      (t) =>
        t.quantity === undefined || t.quantity === null || t.quantity === "",
    )
  )
    missing.push("Ticket quantity");
  return missing;
}

const isPastDay = (d) =>
  d && dayjs.isDayjs(d) && d.isValid() && d.isBefore(dayjs().startOf("day"));

const toDay = (v) =>
  v === null || v === undefined ? v : dayjs.isDayjs(v) ? v : dayjs(v);

// The model is inconsistent with the schedule `value` format: it may be a dayjs,
// a full ISO datetime, "18:00", or "6:00 PM". Extract a valid dayjs time from any
// of these so the TimePicker never receives an "Invalid Date".
const toScheduleTime = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : undefined;

  const str = String(value);
  if (/\d{4}-\d{2}-\d{2}/.test(str)) {
    const asDateTime = dayjs(str);
    if (asDateTime.isValid()) return asDateTime;
  }

  const match = str.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/i);
  if (!match) return undefined;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const meridiem = match[3]?.toLowerCase();
  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;
  return dayjs().hour(hour).minute(minute).second(0).millisecond(0);
};

/**
 * Normalise the canonical model into the native wizard's form-values shape:
 * ISO/dayjs dates become dayjs, past dates are dropped (so the picker forces a
 * fresh pick), and a schedule row is seeded from the start when none exists.
 */
export function mapModelToFormValues(model) {
  const m = { ...(model || {}) };

  m.start_date_time = toDay(m.start_date_time);
  m.end_date_time = toDay(m.end_date_time);
  if (isPastDay(m.start_date_time)) {
    m.start_date_time = undefined;
    m.end_date_time = undefined;
  } else if (isPastDay(m.end_date_time)) {
    m.end_date_time = undefined;
  }

  const start = m.start_date_time;
  const rawSchedule = (m.schedule_info ?? [])
    .map((s) => {
      const date = s?.date ? toDay(s.date) : undefined;
      return {
        ...s,
        date: isPastDay(date) ? undefined : date,
        value: toScheduleTime(s?.value) ?? start,
      };
    })
    .filter((s) => s.date && s.name && s.value);

  const scheduleInfo = rawSchedule.length
    ? rawSchedule
    : start && start.isValid?.()
      ? [{ date: start, name: m.title || "Event", value: start }]
      : [];

  return {
    ...m,
    schedule_info: scheduleInfo,
    end_date_time: m.end_date_time || (start ? start.add(2, "hour") : undefined),
    hsn_code: HSN_CODE,
    location_mode: m.location_mode || "new",
  };
}
